import { useEffect, useRef, useState } from "react";

function useCountUp(target, duration = 1800, start = false) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf;
    const startTime = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - startTime) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [start, target, duration]);
  return value;
}

function Counter({ target, label, prefix = "", suffix = "" }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.4 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  const value = useCountUp(target, 1800, visible);
  return (
    <div ref={ref} className="text-center" data-testid={`stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="text-5xl md:text-6xl font-serif text-[#0F2040] mb-2">
        {prefix}{value.toLocaleString()}{suffix}
      </div>
      <div className="text-xs uppercase tracking-[0.22em] text-gray-500 font-medium">{label}</div>
    </div>
  );
}

export default function StatsCounter({ stats, t }) {
  return (
    <section className="bg-white border-y border-gray-100" data-testid="stats-section">
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-14">
          <span className="editorial-rule mb-5"></span>
          <h2 className="font-serif text-3xl md:text-4xl text-[#0F2040]">{t("home.statsTitle")}</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10">
          <Counter target={stats?.stat_years || 139} label={t("home.stats.years")} />
          <Counter target={stats?.stat_members || 250} label={t("home.stats.members")} prefix="+" />
          <Counter target={stats?.stat_activities || 1200} label={t("home.stats.activities")} prefix="+" />
          <Counter target={stats?.stat_companies || 5000} label={t("home.stats.companies")} prefix="+" />
        </div>
      </div>
    </section>
  );
}
